import React, { useState, useEffect } from 'react';
import { MessageSquare, X, Send } from 'lucide-react';
import { chatAPI } from '../api/axios';

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    maintenance_mode: false,
    maintenance_message: '',
    disabled_agents: [],
    agent_stats: {},
    total_requests: 0,
    total_errors: 0
  });
  const [loading, setLoading] = useState(true);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  
  // Chatbot states
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Try to fetch from API first, fallback to localStorage
      try {
        const response = await chatAPI.getDashboardStatus();
        const data = response.data;
        
        setDashboardData({
          maintenance_mode: data.maintenance_mode || false,
          maintenance_message: data.maintenance_message || '',
          disabled_agents: data.disabled_agents || [],
          agent_stats: data.agent_stats || {},
          total_requests: data.total_requests || 0,
          total_errors: data.total_errors || 0
        });
        
        setMaintenanceMessage(data.maintenance_message || '');
      } catch (apiError) {
        // Fallback to localStorage for display
        const maintenanceData = JSON.parse(localStorage.getItem('admin_maintenance') || '{}');
        const disabledAgents = JSON.parse(localStorage.getItem('admin_disabled_agents') || '[]');
        
        setDashboardData({
          maintenance_mode: maintenanceData.enabled || false,
          maintenance_message: maintenanceData.message || '',
          disabled_agents: disabledAgents,
          agent_stats: {},
          total_requests: 0,
          total_errors: 0
        });
        
        setMaintenanceMessage(maintenanceData.message || '');
      }
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Toggle maintenance mode
  const toggleMaintenanceMode = async () => {
    try {
      const newMaintenanceMode = !dashboardData.maintenance_mode;
      
      const maintenanceData = {
        enabled: newMaintenanceMode,
        message: maintenanceMessage
      };
      
      // Try API first, fallback to localStorage
      try {
        await chatAPI.toggleMaintenance(maintenanceData);
        alert(`Maintenance mode ${newMaintenanceMode ? 'ENABLED' : 'DISABLED'}`);
      } catch (apiError) {
        localStorage.setItem('admin_maintenance', JSON.stringify(maintenanceData));
        alert(`Maintenance mode ${newMaintenanceMode ? 'ENABLED' : 'DISABLED'} (Local Only)\nNote: API connection failed, using local storage.`);
      }
      
      setDashboardData(prev => ({
        ...prev,
        maintenance_mode: newMaintenanceMode,
        maintenance_message: maintenanceMessage
      }));
      
    } catch (err) {
      console.error('Error toggling maintenance mode:', err);
    }
  };

  // Toggle agent status
  const toggleAgent = async (agentName) => {
    try {
      const isCurrentlyDisabled = dashboardData.disabled_agents.includes(agentName);
      
      const newDisabledAgents = isCurrentlyDisabled 
        ? dashboardData.disabled_agents.filter(agent => agent !== agentName)
        : [...dashboardData.disabled_agents, agentName];
      
      const agentData = {
        agent_name: agentName,
        enabled: isCurrentlyDisabled
      };
      
      // Try API first, fallback to localStorage
      try {
        await chatAPI.toggleAgent(agentData);
        const agentDisplayName = agentName.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
        console.log(`Agent ${agentDisplayName} ${isCurrentlyDisabled ? 'ENABLED' : 'DISABLED'}`);
      } catch (apiError) {
        localStorage.setItem('admin_disabled_agents', JSON.stringify(newDisabledAgents));
        const agentDisplayName = agentName.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
        console.log(`Agent ${agentDisplayName} ${isCurrentlyDisabled ? 'ENABLED' : 'DISABLED'} (Local Only)`);
      }
      
      setDashboardData(prev => ({
        ...prev,
        disabled_agents: newDisabledAgents
      }));
      
    } catch (err) {
      console.error('Error toggling agent:', err);
    }
  };

  // Handle chat message sending
  const handleSendMessage = async () => {
    if (!chatInput.trim() || isChatLoading) return;

    if (dashboardData.maintenance_mode) {
      const userMessage = {
        id: `user-${Date.now()}`,
        sender: 'user',
        text: chatInput,
        timestamp: new Date().toLocaleTimeString()
      };

      setChatMessages(prev => [
        ...prev,
        userMessage,
        {
          id: `msg-${Date.now()}`,
          sender: 'Assistant',
          text: `🚧 System is currently under maintenance. ${dashboardData.maintenance_message || 'Please try again later.'}`,
          timestamp: new Date().toLocaleTimeString()
        }
      ]);
      setChatInput('');
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: chatInput,
      timestamp: new Date().toLocaleTimeString()
    };

    setChatMessages(prev => [
      ...prev,
      userMessage,
      { id: `typing-${Date.now()}`, sender: 'Assistant', isTyping: true }
    ]);

    const currentInput = chatInput;
    setChatInput('');
    setIsChatLoading(true);

    try {
      const response = await chatAPI.sendMessage({
        user: { id: 'admin-user', name: 'Admin User' },
        query: currentInput,
        conversation_history: [],
        country: 'KW'
      });

      const data = response.data;
      
      // Check if agent is disabled
      let responseText = data.response || 'Sorry, I couldn\'t process your request.';
      const agentName = data.agent;
      
      if (agentName && dashboardData.disabled_agents?.includes(agentName)) {
        const agentDisplayName = agentName.split('_').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
        responseText = `⚠️ ${agentDisplayName} is currently under maintenance. This service is temporarily unavailable.`;
      }

      setChatMessages(prev => {
        const updated = [...prev];
        updated.pop();
        updated.push({
          id: `msg-${Date.now()}`,
          sender: 'Assistant',
          text: responseText,
          timestamp: new Date().toLocaleTimeString()
        });
        return updated;
      });
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => {
        const updated = [...prev];
        updated.pop();
        updated.push({
          id: `msg-${Date.now()}`,
          sender: 'Assistant',
          text: 'Sorry, there was an error processing your request.',
          timestamp: new Date().toLocaleTimeString()
        });
        return updated;
      });
    } finally {
      setIsChatLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 10000);
    return () => clearInterval(interval);
  }, []);

  const agents = [
    'general_response_agent',
    'flight_booking_agent', 
    'image_analysis_agent',
    'image_display_agent'
  ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f5f7fa',
      padding: '20px',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: '#004a97',
          color: 'white',
          padding: '20px 30px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
              Jazeera Airways ChatBot Control Panel
            </h1>
            <p style={{ margin: '5px 0 0', opacity: 0.8 }}>
              Control ChatbotFixed and AI Agents
            </p>
          </div>
          <button 
            onClick={fetchDashboardData}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 15px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Refresh
          </button>
        </div>

        <div style={{ padding: '30px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #004a97',
                borderRadius: '50%',
                margin: '0 auto',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ marginTop: '20px', color: '#666' }}>Loading...</p>
            </div>
          ) : (
            <>
              {/* System Status */}
              <div style={{
                backgroundColor: dashboardData.maintenance_mode ? '#fff5f5' : '#f0fff4',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '30px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h2 style={{
                    margin: '0 0 10px',
                    color: dashboardData.maintenance_mode ? '#e53e3e' : '#38a169',
                    fontSize: '18px',
                    fontWeight: 'bold'
                  }}>
                    ChatBot Status: {dashboardData.maintenance_mode ? 'Under Maintenance' : 'Online'}
                  </h2>
                  <p style={{ margin: 0, color: '#666' }}>
                    {dashboardData.maintenance_mode 
                      ? `Message: ${dashboardData.maintenance_message}` 
                      : 'All systems operational'}
                  </p>
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                    <input
                      type="text"
                      value={maintenanceMessage}
                      onChange={(e) => setMaintenanceMessage(e.target.value)}
                      placeholder="Maintenance message"
                      style={{
                        padding: '8px 12px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        marginRight: '10px',
                        width: '300px'
                      }}
                    />
                  </div>
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleMaintenanceMode();
                    }}
                    style={{
                      backgroundColor: dashboardData.maintenance_mode ? '#38a169' : '#e53e3e',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '8px 15px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.opacity = '0.8';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.opacity = '1';
                    }}
                  >
                    {dashboardData.maintenance_mode ? 'Enable ChatBot' : 'Enable Maintenance Mode'}
                  </button>
                </div>
              </div>

              {/* Agent Controls */}
              <div style={{ marginBottom: '30px' }}>
                <h2 style={{ margin: '0 0 15px', fontSize: '20px', fontWeight: 'bold' }}>
                  AI Agent Controls
                </h2>
                <div style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '20px'
                }}>
                  <div style={{ fontSize: '14px', color: '#856404', fontWeight: 'bold' }}>
                    ℹ️ Agent Control Status
                  </div>
                  <div style={{ fontSize: '12px', color: '#856404', marginTop: '4px' }}>
                    Agent controls are currently for monitoring only. Backend integration in progress.
                  </div>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: '20px'
                }}>
                  {agents.map(agentName => {
                    const isDisabled = dashboardData.disabled_agents.includes(agentName);
                    const displayName = agentName.split('_').map(word => 
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ');
                    
                    return (
                      <div key={agentName} style={{
                        backgroundColor: isDisabled ? '#f7fafc' : 'white',
                        border: `1px solid ${isDisabled ? '#e2e8f0' : '#edf2f7'}`,
                        borderRadius: '8px',
                        padding: '20px',
                        boxShadow: isDisabled ? 'none' : '0 2px 5px rgba(0, 0, 0, 0.05)'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '15px'
                        }}>
                          <h3 style={{
                            margin: 0,
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: isDisabled ? '#a0aec0' : '#2d3748'
                          }}>
                            {displayName}
                          </h3>
                          <div style={{
                            backgroundColor: isDisabled ? '#fed7d7' : '#c6f6d5',
                            color: isDisabled ? '#e53e3e' : '#38a169',
                            padding: '4px 10px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {isDisabled ? 'Disabled' : 'Active'}
                          </div>
                        </div>
                        
                        <button 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleAgent(agentName);
                          }}
                          style={{
                            width: '100%',
                            backgroundColor: isDisabled ? '#4299e1' : '#f56565',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '8px 0',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.opacity = '0.8';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.opacity = '1';
                          }}
                        >
                          {isDisabled ? 'Enable Agent' : 'Disable Agent'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Test ChatBot */}
      <div
        onClick={() => {
          setIsChatOpen(!isChatOpen);
          if (!isChatOpen && chatMessages.length === 0) {
            setChatMessages([{
              id: 'welcome',
              sender: 'Assistant',
              text: 'Hi! This is the test chatbot to verify admin controls are working.',
              timestamp: new Date().toLocaleTimeString()
            }]);
          }
        }}
        style={{
          position: 'fixed',
          bottom: '30px',
          right: '30px',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(160deg, rgb(24, 75, 146) 30%, rgb(0, 28, 131) 100%)',
          borderRadius: '50px',
          padding: '12px 16px',
          cursor: 'pointer',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0, 28, 131, 0.3)',
          gap: '8px'
        }}
      >
        <MessageSquare size={24} color="white" />
        <span style={{
          color: 'white',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          Test ChatBot
        </span>
      </div>

      {/* Chat Window */}
      {isChatOpen && (
        <div style={{
          position: 'fixed',
          bottom: '100px',
          right: '30px',
          width: '350px',
          height: '450px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1001,
          overflow: 'hidden'
        }}>
          <div style={{
            backgroundColor: '#004a97',
            color: 'white',
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px' }}>Test ChatBot</h3>
              <p style={{ margin: 0, fontSize: '12px', opacity: 0.8 }}>Admin Control Test</p>
            </div>
            <button
              onClick={() => setIsChatOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              <X size={20} />
            </button>
          </div>

          <div style={{
            flex: 1,
            padding: '16px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {chatMessages.map((msg, idx) => (
              <div key={idx} style={{
                display: 'flex',
                justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start'
              }}>
                <div style={{
                  maxWidth: '80%',
                  padding: '8px 12px',
                  borderRadius: msg.sender === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                  backgroundColor: msg.sender === 'user' ? '#004a97' : '#f3f4f6',
                  color: msg.sender === 'user' ? 'white' : '#374151',
                  fontSize: '14px',
                  wordWrap: 'break-word'
                }}>
                  {msg.isTyping ? (
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <div style={{
                        width: '6px', height: '6px', borderRadius: '50%',
                        backgroundColor: '#666', animation: 'pulse 1.5s infinite'
                      }} />
                      <div style={{
                        width: '6px', height: '6px', borderRadius: '50%',
                        backgroundColor: '#666', animation: 'pulse 1.5s infinite 0.2s'
                      }} />
                      <div style={{
                        width: '6px', height: '6px', borderRadius: '50%',
                        backgroundColor: '#666', animation: 'pulse 1.5s infinite 0.4s'
                      }} />
                    </div>
                  ) : (
                    <div>
                      {msg.text}
                      {msg.timestamp && (
                        <div style={{
                          fontSize: '10px',
                          opacity: 0.7,
                          marginTop: '4px'
                        }}>
                          {msg.timestamp}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={{
            padding: '12px 16px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            gap: '8px'
          }}>
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !isChatLoading) {
                  handleSendMessage();
                }
              }}
              placeholder={dashboardData.maintenance_mode ? 'System under maintenance...' : 'Type your message...'}
              style={{
                flex: 1,
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '20px',
                outline: 'none',
                fontSize: '14px',
                backgroundColor: dashboardData.maintenance_mode ? '#f9f9f9' : 'white'
              }}
              disabled={isChatLoading || dashboardData.maintenance_mode}
            />
            <button
              onClick={handleSendMessage}
              disabled={!chatInput.trim() || isChatLoading || dashboardData.maintenance_mode}
              style={{
                backgroundColor: (chatInput.trim() && !isChatLoading && !dashboardData.maintenance_mode) ? '#004a97' : '#9ca3af',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: (chatInput.trim() && !isChatLoading && !dashboardData.maintenance_mode) ? 'pointer' : 'not-allowed'
              }}
            >
              {isChatLoading ? (
                <div style={{
                  width: '12px', height: '12px',
                  border: '2px solid transparent',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              ) : (
                <Send size={16} />
              )}
            </button>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </div>
  );
};

export default AdminDashboard;